/** Groups a saved rule the same way convertRulesToTrail does. OR stays on this level. AND starts the next level. */
export function groupRuleApprovers(from = []) {
	const levels = [];
	let current = [];
	from.forEach((approver, index) => {
		if (index === 0) {
			current = [approver];
			return;
		}
		if (from[index - 1]?.nextJoin === "OR") {
			current.push(approver);
		} else {
			if (current.length) levels.push(current);
			current = [approver];
		}
	});
	if (current.length) levels.push(current);
	return levels;
}

const labelOf = (approver) => approver?.label || "Approver";

const joinOr = (level = []) => {
	const names = level.map(labelOf);
	if (names.length <= 1) return names[0] || "the approver";
	if (names.length === 2) return `${names[0]} or ${names[1]}`;
	return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
};

/** Plain sentence for a travel approval rule, before it is saved onto a request. */
export function describeApprovalRule(from = [], order = "SEQUENCE") {
	const levels = groupRuleApprovers(from);
	if (!levels.length) return "";
	const sequence = order !== "PARALLEL";
	const first = levels[0];
	const firstWho = first.length > 1 ? `Any one of ${joinOr(first)}` : labelOf(first[0]);

	if (levels.length === 1) {
		if (first.length === 1) return `${labelOf(first[0])} approves this request.`;
		return `${firstWho} can approve. One approval is enough.`;
	}

	if (!sequence) {
		const orNote = levels.some((level) => level.length > 1) ? " On a step with more than one person, any one of them can decide that step." : "";
		return `These steps are asked at the same time. Every step must approve.${orNote}`;
	}

	const second = levels[1];
	const secondWho = second.length > 1 ? `any one of ${joinOr(second)}` : labelOf(second[0]);
	const firstSentence = first.length > 1 ? `${firstWho} can approve the first step. One of them is enough.` : `${firstWho} approves the first step.`;
	const later = levels.length > 2 ? ` After that, the remaining steps are asked one at a time.` : ` After that step approves, ${secondWho} is asked.`;
	return `${firstSentence}${later} If a step is declined, the request stops.`;
}

const levelAction = (level = []) => {
	const decided = level.find((slot) => slot?.action && slot.action !== "pending");
	return decided?.action || "pending";
};

/** Card and request-status text for an approval that is still open. */
export function approvalQueueCopy(trail, inSequence) {
	const levels = Array.isArray(trail) ? trail.filter((level) => Array.isArray(level)) : [];
	const several = levels.length > 1 || levels.some((level) => level.length > 1);
	if (!several) return null;

	const states = levels.map(levelAction);
	const currentIndex = states.findIndex((state) => state === "pending");
	const approved = states.filter((state) => state === "approved").length;
	const stepNumber = (currentIndex >= 0 ? currentIndex : states.length - 1) + 1;
	const anyOne = currentIndex >= 0 && levels[currentIndex].length > 1;
	const sequence = inSequence === true && levels.length > 1;
	if (levels.length === 1) {
		return { badge: "Your turn", note: "Any one of these approvers is enough" };
	}

	let note = sequence ? `In sequence. Step ${stepNumber} of ${levels.length}` : `At the same time`;
	if (sequence && approved > 0) {
		note += approved === 1 ? ". The earlier step is already approved" : `. ${approved} earlier steps are already approved`;
	} else if (sequence && levels.length > 1) {
		note += ". The next approver is asked after this step approves";
	} else if (!sequence && levels.length > 1) {
		note += ". Every group must approve";
	}
	if (anyOne) note += ". Any one person in the group is enough";

	return { badge: "Your turn", note };
}

const personName = (person) => {
	const name = `${person?.fName || ""} ${person?.lName || ""}`.trim();
	return name || person?.label || "the approver";
};

const stepWho = (step) => {
	const names = (step?.people || []).map(personName);
	if (names.length <= 1) return names[0] || "the approver";
	if (names.length === 2) return `${names[0]} or ${names[1]}`;
	return `${names.slice(0, -1).join(", ")}, or ${names[names.length - 1]}`;
};

/** Sentence above the approver list inside an open request. */
export function describeApprovalSteps(steps = [], inSequence = false) {
	if (!steps.length) return "";
	if (steps.length === 1 && steps[0].anyOne) {
		return `Any one of ${stepWho(steps[0])} can approve. One approval is enough.`;
	}
	if (steps.length === 1) return "";

	const sequence = inSequence === true;
	if (steps.every((step) => step.action === "approved")) {
		const names = steps.map((step) => (step.anyOne ? `any one of ${stepWho(step)}` : stepWho(step)));
		if (!sequence) return `${names.join(" and ")} approved.`;
		if (names.length === 2) return `${names[0]} approved the first step. ${names[1]} then approved.`;
		return `All ${names.length} steps are approved.`;
	}
	if (steps.some((step) => step.action === "rejected")) {
		return "A step was declined, so this request stopped.";
	}
	if (!sequence) {
		const anyGroup = steps.some((step) => step.anyOne);
		return anyGroup
			? "Every group must approve. Inside a group, any one person is enough."
			: "Every approver below must approve. They are not asked one after another.";
	}

	const current = steps.find((step) => step.current);
	const approved = steps.filter((step) => step.action === "approved");
	if (approved.length && current) {
		const earlier = approved.map((step) => (step.anyOne ? `any one of ${stepWho(step)}` : stepWho(step))).join(", ");
		const waiting = current.anyOne ? `any one of ${stepWho(current)}` : stepWho(current);
		return `${earlier} already approved. The next approver is ${waiting}.`;
	}

	const first = steps[0];
	const firstWho = first.anyOne ? `Any one of ${stepWho(first)}` : stepWho(first);
	const second = steps[1];
	const secondWho = second?.anyOne ? `any one of ${stepWho(second)}` : stepWho(second);
	const firstSentence = first.anyOne ? `${firstWho} can approve the first step. One of them is enough.` : `${firstWho} approves the first step.`;
	return `${firstSentence} After that step approves, ${secondWho} is asked. If a step is declined, the request stops.`;
}
